import React from 'react';
import { Link } from 'react-router-dom';
import useFadeIn from '../hooks/useFadeIn';
import { buildSignupPath, buildSignupState, captureCtaClick } from '../utils/analytics';

const values = [
  {
    title: 'Mission-Driven',
    description: 'We judge every decision by whether it improves medication access for patients.',
    gradient: 'from-blue-500 to-cyan-400',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v4l3 3"/>
      </svg>
    ),
  },
  {
    title: 'Built with Love',
    description: 'We build with care, urgency, and respect for the people navigating healthcare every day.',
    gradient: 'from-fuchsia-500 to-pink-400',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
      </svg>
    ),
  },
  {
    title: 'Transparency First',
    description: 'Patients and providers deserve clear information with no hidden costs or confusion.',
    gradient: 'from-violet-500 to-purple-400',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    ),
  },
];

function AboutPage() {
  const [heroRef, heroVisible]     = useFadeIn(0.05);
  const [storyRef, storyVisible]   = useFadeIn();
  const [valuesRef, valuesVisible] = useFadeIn(0.08);
  const [ctaRef, ctaVisible]       = useFadeIn();

  return (
    <div className="bg-white">

      {/* ── Hero ─────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-blue-100/60 via-purple-50/40 to-transparent blur-3xl ambient-glow pointer-events-none" />

        <div
          ref={heroRef}
          className={`fade-in-section ${heroVisible ? 'is-visible' : ''} relative z-10 flex flex-col items-center text-center px-8 md:px-16 pt-16 pb-16 md:pt-24 md:pb-24 max-w-[1440px] mx-auto`}
        >
          <p className="text-[13px] text-[#86868b] tracking-widest uppercase font-medium mb-6">
            About MedHug
          </p>
          <h1 className="text-5xl md:text-6xl xl:text-[72px] xl:leading-[1.05] text-[#1d1d1f] tracking-tight max-w-[860px] [text-wrap:balance]">
            Healthcare, reimagined with{' '}
            <span className="gradient-text">purpose</span>
          </h1>
          <p className="mt-7 text-xl text-[#6e6e73] max-w-xl leading-relaxed font-normal">
            A Public Benefit Corporation on a mission to make medications accessible, affordable, and understandable for all.
          </p>
        </div>
      </section>

      {/* ── Story ────────────────────────────────────── */}
      <section className="bg-[#f5f5f7] px-8 md:px-16 py-16 md:py-24">
        <div
          ref={storyRef}
          className={`fade-in-section ${storyVisible ? 'is-visible' : ''} max-w-[900px] mx-auto flex flex-col gap-10`}
        >
          <p className="text-[13px] text-[#86868b] tracking-widest uppercase font-medium">
            Our Story
          </p>

          <h2 className="text-3xl md:text-4xl xl:text-[44px] xl:leading-[1.15] text-[#1d1d1f] [text-wrap:balance]">
            Started at Stanford. Built for everyone.
          </h2>

          <div className="flex flex-col gap-6">
            <p className="text-lg text-[#6e6e73] leading-relaxed font-normal">
              MedHug was founded by a Stanford PhD graduate who witnessed firsthand how complexity in the prescription system leads to real harm — delayed treatments, unnecessary costs, and frustrated providers unable to spend time on what matters most: patient care.
            </p>
            <p className="text-lg text-[#6e6e73] leading-relaxed font-normal">
              We built MedHug to eliminate the friction. By providing instant insurance coverage information and cost-effective prescribing options at the point of care, we help healthcare teams make faster, smarter decisions — so patients get the right medications without the wait.
            </p>
            <p className="text-lg text-[#6e6e73] leading-relaxed font-normal">
              As a Public Benefit Corporation, our commitment to patients is embedded in our structure — not just our values.
            </p>
          </div>
        </div>
      </section>

      {/* ── Values ───────────────────────────────────── */}
      <section className="bg-white px-8 md:px-16 py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto flex flex-col gap-14">

          <div
            ref={valuesRef}
            className={`fade-in-section ${valuesVisible ? 'is-visible' : ''} flex flex-col items-center text-center gap-5 max-w-2xl lg:max-w-none mx-auto`}
          >
            <p className="text-[13px] text-[#86868b] tracking-widest uppercase font-medium">
              What We Stand For
            </p>
            <h2 className="text-3xl md:text-4xl xl:text-[44px] xl:leading-[1.15] text-[#1d1d1f] lg:whitespace-nowrap">
              Principles that guide everything we do
            </h2>
          </div>

          <div className={`stagger-children ${valuesVisible ? 'is-visible' : ''} grid grid-cols-1 md:grid-cols-3 gap-6`}>
            {values.map((item, i) => (
              <div
                key={i}
                className="stagger-child flex flex-col gap-4 p-6 md:gap-5 md:p-10 rounded-3xl bg-[#f5f5f7] hover:bg-[#ebebee] transition-all duration-500"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shadow-lg shadow-black/10`}>
                  {item.icon}
                </div>
                <h3 className="text-[22px] font-semibold text-[#1d1d1f] mt-1">
                  {item.title}
                </h3>
                <p className="text-[15px] text-[#6e6e73] leading-relaxed font-normal">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#1d1d1f] px-8 md:px-16 py-16 md:py-24">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-blue-600/15 via-purple-600/10 to-transparent blur-3xl pointer-events-none" />

        <div
          ref={ctaRef}
          className={`fade-in-section ${ctaVisible ? 'is-visible' : ''} relative z-10 max-w-[1440px] mx-auto flex flex-col items-center text-center gap-8`}
        >
          <h2 className="text-3xl md:text-4xl xl:text-[48px] xl:leading-[1.1] text-white max-w-[640px] lg:max-w-none lg:whitespace-nowrap">
            Want to learn more about our work?
          </h2>
          <p className="text-lg text-[#d1d1d6] leading-relaxed max-w-md font-normal">
            We'd love to walk you through what we're building and how it can help your team.
          </p>
          <div className="flex items-center gap-5 mt-4">
            <Link
              to={buildSignupPath()}
              state={buildSignupState('about-book-demo')}
              data-ph-capture-attribute-cta="about-book-demo"
              onClick={() => captureCtaClick('about-book-demo', { placement: 'about-cta' })}
              className="bg-white px-8 py-[14px] text-[15px] font-medium text-[#1d1d1f] hover:bg-gray-100 transition-all whitespace-nowrap rounded-full shadow-lg hover:-translate-y-[1px]"
            >
              Book a Demo
            </Link>
            <a
              href="mailto:yiwolf@medhug.org"
              className="px-8 py-[14px] text-[15px] font-medium text-[#2997ff] hover:text-[#6cb6ff] transition-all whitespace-nowrap"
            >
              Get in Touch →
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}

export default AboutPage;
